import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Avatar,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  InputAdornment,
  IconButton,
  Card,
} from '@mui/material';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { useInvalidation } from 'api-read-hook';
import { toast } from 'sonner';
import useApiCallable from '../../api/use-api-callable';
import { Api } from '../../api';
import { toastErrorHandler } from '../../util';
import { ScreenError, SoloLoading } from '..';
import { serverImageUrl } from '../../util';

interface ReassignCoachModalProps {
  open: boolean;
  onClose: () => void;
  userName: string;
  currentClinicianId?: string;
  targetType: 'coach' | 'physician';
  userId?: string;
}

export default function ReassignCoachModal({
  open,
  onClose,
  userName,
  currentClinicianId,
  targetType,
  userId,
}: ReassignCoachModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      userName={userName}
      currentClinicianId={currentClinicianId}
      targetType={targetType}
      userId={userId}
    />
  );
}

function Modal({
  open,
  onClose,
  userName,
  currentClinicianId,
  targetType,
  userId,
}: ReassignCoachModalProps) {
  const params = useParams();
  const effectiveUserId = userId || params.id || '';
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedClinician, setSelectedClinician] =
    useState<Api.User.V_ClinicianListAsAdmin | null>(null);
  const { invalidateExact } = useInvalidation();
  const callApi = useApiCallable();

  const handleAssign = async () => {
    if (selectedClinician) {
      const body =
        targetType === 'coach'
          ? { coach: selectedClinician['@id'] }
          : { physician: selectedClinician['@id'] };
      if (!effectiveUserId) return;
      const resp = await callApi(
        `/users/${effectiveUserId}/as-admin`,
        { method: 'PATCH', jsonBody: body },
        { errorHandler: toastErrorHandler },
      );
      if (resp) {
        invalidateExact(`/users/${effectiveUserId}/stats`);
        if (currentClinicianId) {
          invalidateExact(`/clinicians/${currentClinicianId}/clients`);
        }
        toast.success(
          `${targetType === 'coach' ? 'Coach' : 'Physician'} reassigned`,
        );
        onClose();
        setSelectedClinician(null);
        setSearchQuery('');
        setPage(1);
      }
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedClinician(null);
    setSearchQuery('');
    setPage(1);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">
          Select the {targetType === 'coach' ? 'coach' : 'physician'} to whom
          you want to assign {userName}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body1">
            The user will be notified about this change
          </Typography>

          <TextField
            fullWidth
            placeholder="Search by name"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlass size={20} />
                </InputAdornment>
              ),
            }}
          />

          <CliniciansTable
            currentClinicianId={currentClinicianId}
            targetType={targetType}
            searchQuery={searchQuery}
            page={page}
            setPage={setPage}
            selectedClinician={selectedClinician}
            setSelectedClinician={setSelectedClinician}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedClinician}
        >
          Assign User
        </Button>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CliniciansTable({
  currentClinicianId,
  targetType,
  searchQuery,
  page,
  setPage,
  selectedClinician,
  setSelectedClinician,
}: {
  currentClinicianId?: string;
  targetType: 'coach' | 'physician';
  searchQuery: string;
  page: number;
  setPage: (page: number) => void;
  selectedClinician: Api.User.V_ClinicianListAsAdmin | null;
  setSelectedClinician: (
    clinician: Api.User.V_ClinicianListAsAdmin | null,
  ) => void;
}) {
  const apiParams = {
    'clinicianProfile.type': targetType === 'coach' ? 'coach' : 'physician',
    'clinicianProfile.isActive': true,
    ...(searchQuery && { search: searchQuery }),
    page,
  };

  const api = Api.User.useReadCollectionClinicians(apiParams);

  if (api.error) return <ScreenError {...api} />;
  if (!api.data) return <SoloLoading />;

  const clinicians = api.data.member || [];
  const filteredClinicians = currentClinicianId
    ? clinicians.filter(
        (c) => c['@id'] !== currentClinicianId && c.id !== currentClinicianId,
      )
    : clinicians;

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClinicians.length > 0 ? (
              filteredClinicians.map((clinician) => (
                <TableRow
                  key={clinician.id}
                  hover
                  selected={selectedClinician?.id === clinician.id}
                  onClick={() => setSelectedClinician(clinician)}
                  sx={{
                    cursor: 'pointer',
                    '&.Mui-selected': {
                      backgroundColor: '#F3F2FF',
                      '&:hover': {
                        backgroundColor: '#F3F2FF',
                      },
                    },
                  }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        src={
                          clinician.displayPicture
                            ? serverImageUrl({
                                id: clinician.displayPicture.id,
                              })
                            : undefined
                        }
                        sx={{ width: 40, height: 40 }}
                      >
                        {(clinician.name || clinician.email).charAt(0)}
                      </Avatar>
                      <Typography variant="body2">
                        {clinician.name || clinician.email}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {clinician.clinicianProfile?.type === 'coach'
                        ? 'Health Coach'
                        : 'Lifestyle Medicine Physician'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No {targetType === 'coach' ? 'coaches' : 'physicians'}{' '}
                    available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter
            sx={{
              borderTop: '1px solid rgba(224, 224, 224, 1)',
            }}
          >
            <TableRow>
              <TablePagination
                count={api.data?.totalItems ?? 0}
                page={page - 1}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                rowsPerPage={10}
                colSpan={2}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Card>
  );
}
